import type {
	SearchMiddleware,
	SearchSchemaInput
} from '@tanstack/react-router'
import { stripSearchParams } from '@tanstack/react-router'
import { z } from 'zod'
import type { TKey } from '@/lib/i18n'
import {
	NODE_CATEGORIES,
	readCategories,
	readPeople,
	type UnreadableSearchValue
} from '@/modules/file-manager/lib/folder-search'

/**
 * An address parameter this route could not read, named by the parameter
 * itself rather than by `FolderSearch`'s: `scope` is not one of its four, and
 * widening that interface for one more caller would be the wrong direction —
 * see `readSearchResultsSearch` for how the two meet.
 */
export interface SearchResultsUnreadableValue {
	param: 'categories' | 'people' | 'scope'
	value: unknown
}

/**
 * The scopes this address can name — the same three `@/modules/search/lib/types`'s
 * `SearchScope` names, in the words an address accepts rather than the shape
 * a popover constructs: an address is read more forgivingly than a popover
 * is built, so a hand-written link naming a scope has to fail the same way
 * an invalid category does, as an unreadable parameter, not as a type
 * error. This stays a separate list rather than an import of that type for
 * exactly that reason.
 */
export const SEARCH_RESULT_SCOPES = ['all', 'folder', 'shared'] as const

/**
 * Each scope in the person's own words, for the empty state: it names where a
 * search looked so that nobody concludes the file does not exist. Exhaustive
 * over `SEARCH_RESULT_SCOPES` for `CATEGORY_LABELS`'s reason — a scope this
 * list gains without a label here is a compile error, not a blank spot on
 * screen.
 *
 * **`folder` names no folder by name here.** The label is static —
 * "this folder" rather than the folder the address's own `scopeId` points
 * at — because naming it would mean this empty state resolving
 * `scopeId` into a node itself, a second reader of
 * `folderAncestorsQueryOptions` next to the one the field already runs. A
 * generic word is what T32.4 owes; a page that names the folder by name is
 * T32.5's own reach, not reopened here.
 */
export const SEARCH_RESULT_SCOPE_LABELS = {
	all: 'client:search.results_page.scope.all',
	folder: 'client:search.results_page.scope.folder',
	shared: 'client:search.results_page.scope.shared'
} as const satisfies Record<(typeof SEARCH_RESULT_SCOPES)[number], TKey>

/**
 * A search's address: the same two union filters MS27 fixed for a folder
 * listing, plus the term and the scope a folder listing has neither of.
 *
 * **No `by`/`direction`.** T32.3 fixes the ranking as deterministic and
 * server-decided — exact match, then prefix, then substring, trigram
 * similarity as the tiebreak inside each band — so there is no order for a
 * person to choose and nothing here to carry one.
 *
 * Every field defaults, for `folderSearchSchema`'s reason: a link naming one
 * of them typechecks without spelling out the rest.
 */
export const searchResultsSearchSchema = z.object({
	categories: z.array(z.enum(NODE_CATEGORIES)).default([]),
	people: z.array(z.string()).default([]),
	q: z.string().default(''),
	scope: z.enum(SEARCH_RESULT_SCOPES).default('all'),
	/**
	 * The folder `scope: 'folder'` names, and nothing outside that scope
	 * reads it: `null` under every other scope is the canonical spelling, so
	 * two links narrowed the same way still normalise to one address (the
	 * property `search-results-address.spec.ts` holds for `categories` and
	 * `people`). `readScopeId` below is what enforces that, not this schema —
	 * this only states the shape.
	 */
	scopeId: z.string().nullable().default(null)
})

export type SearchResultsSearch = z.infer<typeof searchResultsSearchSchema>

/**
 * The page as it is drawn when nothing narrows it: an empty term, the default
 * scope, no filters. Parsed from the schema for `DEFAULT_LISTING_SEARCH`'s
 * reason — one home for the defaults, so `stripSearchParams` below and every
 * reader of this file agree on what "nothing was asked" looks like.
 */
export const DEFAULT_SEARCH_RESULTS_SEARCH = searchResultsSearchSchema.parse({})

export interface SearchResultsSearchReading {
	search: SearchResultsSearch
	unreadable: SearchResultsUnreadableValue[]
}

/**
 * Reads an address into a search, keeping every value it recognises and
 * reporting what it dropped, for `readListingSearch`'s reason: a link pasted
 * into a chat and mangled by the line wrap opens the readable half of the
 * search rather than an error screen.
 *
 * `categories` and `people` are read by the exact functions the folder
 * listing reads them with, imported rather than restated, so the two screens
 * cannot drift on what "the same set in a different order" means. They report
 * onto a `FolderSearch`-shaped array because that is the signature they were
 * written against; this function only ever calls them for those same two
 * params, so narrowing `keyof FolderSearch` down to the two literals this
 * route asked for is reading back a fact those two bodies guarantee, not
 * guessing one.
 *
 * `q` is never unreadable. It is free text with no vocabulary to fail
 * against, and a malformed value (an address built by hand as `?q[]=`) is
 * simply not a string — coerced to the empty search a person would notice at
 * a glance in the field itself, which is not true of an invisible category or
 * person id.
 */
export function readSearchResultsSearch(
	input: unknown
): SearchResultsSearchReading {
	const raw =
		typeof input === 'object' && input !== null
			? (input as Record<string, unknown>)
			: {}
	const folderUnreadable: UnreadableSearchValue[] = []
	const categories = readCategories(raw.categories, folderUnreadable)
	const people = readPeople(raw.people, folderUnreadable)
	const unreadable: SearchResultsUnreadableValue[] = folderUnreadable.map(
		(entry) => ({
			param: entry.param as 'categories' | 'people',
			value: entry.value
		})
	)
	const { scope, scopeId } = readScopeAndFolder(
		raw.scope,
		raw.scopeId,
		unreadable
	)

	return {
		search: {
			categories,
			people,
			q: typeof raw.q === 'string' ? raw.q : '',
			scope,
			scopeId
		},
		unreadable
	}
}

/** What the route validates its address with, `validateListingSearch`'s twin. */
export function validateSearchResultsSearch(
	search: Partial<SearchResultsSearch> & SearchSchemaInput
): SearchResultsSearch {
	return readSearchResultsSearch(search).search
}

/**
 * What the route builds its address with, `listingSearchMiddlewares`'s twin
 * and for the same two-halves reason: canonical on the way out, forgiving on
 * the way in.
 */
export const searchResultsSearchMiddlewares: SearchMiddleware<SearchResultsSearch>[] =
	[
		({ next, search }) => next(readSearchResultsSearch(search).search),
		stripSearchParams<SearchResultsSearch>(DEFAULT_SEARCH_RESULTS_SEARCH)
	]

/**
 * Each address parameter this route can report as unreadable, in the
 * person's own words. `q` has no entry: it is never pushed onto `unreadable`
 * above, so the notice never has to name it.
 */
export const SEARCH_RESULTS_PARAM_LABELS = {
	categories: 'client:files.filters.category.label',
	people: 'client:files.filters.people.label',
	scope: 'client:search.results_page.scope_label'
} as const satisfies Record<SearchResultsUnreadableValue['param'], TKey>

/**
 * The scope and the folder it names, settled together — which is the whole
 * point of it being one function.
 *
 * **They were two, and that is what OPSSTD-7724 was.** A scope reading
 * `folder` and a `scopeId` reading `null` is not a narrower search missing a
 * detail: it is a search nobody can run. The reader that settled the scope
 * alone let that pair through, the query factory downstream quietly widened
 * it to every file, and the empty state went on interpolating the raw
 * `scope` field — so the page searched the whole tree while naming a folder.
 * Two readers, two answers, and the wrong one on screen.
 *
 * So the pair is decided once. A scope outside the closed list opens the page
 * on the default and is reported, the same way an invalid category is.
 *
 * `folder` with no folder to point at is reported **as an unreadable scope**,
 * not as a missing id: it is the scope that could not be honoured, and
 * `scope` is the word the notice already has a label for.
 *
 * A `scopeId` under any other scope is dropped without a note. There the two
 * facts genuinely do not conflict, because the other scopes have no folder to
 * have narrowed, and dropping it is what keeps two links that disagree on a
 * stray id normalising to one address.
 */
function readScopeAndFolder(
	rawScope: unknown,
	rawScopeId: unknown,
	unreadable: SearchResultsUnreadableValue[]
): Pick<SearchResultsSearch, 'scope' | 'scopeId'> {
	const fallback = {
		scope: DEFAULT_SEARCH_RESULTS_SEARCH.scope,
		scopeId: null
	}

	if (rawScope === undefined) {
		return fallback
	}

	if (
		typeof rawScope !== 'string' ||
		!(SEARCH_RESULT_SCOPES as readonly string[]).includes(rawScope)
	) {
		unreadable.push({ param: 'scope', value: rawScope })

		return fallback
	}

	const scope = rawScope as SearchResultsSearch['scope']

	if (scope !== 'folder') {
		return { scope, scopeId: null }
	}

	if (typeof rawScopeId === 'string' && rawScopeId.length > 0) {
		return { scope, scopeId: rawScopeId }
	}

	unreadable.push({ param: 'scope', value: rawScope })

	return fallback
}
